import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import KitchenIcon from "@mui/icons-material/Kitchen";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Button } from "@mui/material";
import { deleteCabinetQuote, getCabinetFormTemplateConfig, getCabinetQuotes } from "./api";
import { CABINET_QUOTE_STATUSES, normalizeCabinetQuoteStatuses } from "./constants";
import { CQ_FIELD, cqStatusClass } from "./ui";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, StatTile } from "../../components/UI/States";

const SORT_OPTIONS = [
  { value: "updated_at:desc", label: "Last updated (newest)" },
  { value: "updated_at:asc", label: "Last updated (oldest)" },
  { value: "created_at:desc", label: "Created (newest)" },
  { value: "title:asc", label: "Title (A-Z)" },
  { value: "status:asc", label: "Status (A-Z)" },
];

const getStatusClass = cqStatusClass;

const formatStatusLabel = (status) =>
  String(status || "draft")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatUpdated = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

const newQuoteButton = (
  <Button component={Link} to="/cabinet-quotes/new" variant="contained" startIcon={<AddIcon />}>
    New Quote
  </Button>
);

function CabinetQuoteList() {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const userId = user?.userID;
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownershipFilter, setOwnershipFilter] = useState("all");
  const [sortValue, setSortValue] = useState("updated_at:desc");
  const [quoteStatuses, setQuoteStatuses] = useState([...CABINET_QUOTE_STATUSES]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!userId) {
      setQuotes([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    const [sortBy, sortDirection] = sortValue.split(":");
    getCabinetQuotes({
      userId,
      search: debouncedSearch,
      status: statusFilter === "all" ? "" : statusFilter,
      ownership: ownershipFilter,
      sortBy,
      sortDirection,
    })
      .then((response) => {
        if (!active) return; // ignore stale responses from rapid filter changes
        setQuotes(Array.isArray(response) ? response : []);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [userId, debouncedSearch, statusFilter, ownershipFilter, sortValue]);

  useEffect(() => {
    if (!userId) {
      setQuoteStatuses([...CABINET_QUOTE_STATUSES]);
      return;
    }

    getCabinetFormTemplateConfig(userId)
      .then((config) => {
        setQuoteStatuses(normalizeCabinetQuoteStatuses(config?.quote_statuses));
      })
      .catch(() => {
        setQuoteStatuses([...CABINET_QUOTE_STATUSES]);
      });
  }, [userId]);

  const onDeleteQuote = async (event, quoteId) => {
    event.stopPropagation();
    if (!user?.userID || !quoteId) return;
    const confirmed = window.confirm("Delete this quote? This action cannot be undone.");
    if (!confirmed) return;

    try {
      await deleteCabinetQuote(quoteId, user.userID);
      setQuotes((prev) => prev.filter((quote) => quote._id !== quoteId));
    } catch (deleteError) {
      window.alert(deleteError?.message || "Unable to delete quote");
    }
  };

  const quoteStats = useMemo(() => {
    const countBy = (status) => quotes.filter((quote) => quote.status === status).length;
    return {
      total: quotes.length,
      pendingCustomer: countBy("pending_customer"),
      approved: countBy("approved"),
      draft: countBy("draft"),
    };
  }, [quotes]);

  const hasActiveFilters = Boolean(debouncedSearch) || statusFilter !== "all" || ownershipFilter !== "all";

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={KitchenIcon}
        tone="amber"
        eyebrow="Cabinets"
        title="Cabinet Quotes"
        description="Track quotes, collaborate with the team, and act on customer decisions."
        actions={newQuoteButton}
      />

      <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Total quotes" value={quoteStats.total} />
        <StatTile label="Pending customer" value={quoteStats.pendingCustomer} tone="sky" />
        <StatTile label="Approved" value={quoteStats.approved} tone="emerald" />
        <StatTile label="Drafts" value={quoteStats.draft} />
      </div>

      <div className="app-card flex flex-col gap-3 rounded-2xl border p-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <SearchIcon className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" fontSize="small" />
          <input
            className={`${CQ_FIELD} pl-9`}
            placeholder="Search quote #, title, customer, or owner…"
            aria-label="Search quotes"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:flex lg:items-center">
          <select
            className={`${CQ_FIELD} lg:w-auto`}
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">All statuses</option>
            {quoteStatuses.map((status) => (
              <option key={status} value={status}>
                {formatStatusLabel(status)}
              </option>
            ))}
          </select>
          <select
            className={`${CQ_FIELD} lg:w-auto`}
            aria-label="Filter by ownership"
            value={ownershipFilter}
            onChange={(event) => setOwnershipFilter(event.target.value)}
          >
            <option value="all">Owned + collaborating</option>
            <option value="owned">Owned by me</option>
            <option value="collaborating">Collaborating only</option>
          </select>
          <select
            className={`${CQ_FIELD} lg:w-auto`}
            aria-label="Sort quotes"
            value={sortValue}
            onChange={(event) => setSortValue(event.target.value)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="app-card overflow-hidden rounded-2xl border">
        <div className="overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                <th>Quote</th>
                <th>Customer</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Checklist</th>
                <th>Updated</th>
                <th className="!text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                quotes.length === 0 &&
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={`skeleton_${index}`}>
                    <td colSpan={7}>
                      <div className="app-skeleton h-5 w-full" />
                    </td>
                  </tr>
                ))}

              {!loading &&
                quotes.map((quote) => {
                  const total = Number(quote.checklist_total || 0);
                  const done = Number(quote.checklist_done || 0);
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                  return (
                    <tr
                      key={quote._id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/cabinet-quotes/${quote._id}`)}
                    >
                      <td className="!align-top">
                        <div className="font-medium">{quote.title || "Untitled Quote"}</div>
                        <div className="app-muted text-xs tabular-nums">{quote.quote_number || quote._id}</div>
                      </td>
                      <td className="!align-top">
                        <div className="font-medium">{quote.customer_name || "—"}</div>
                        <div className="app-muted text-xs tabular-nums">{quote.customer_phone || "No phone"}</div>
                        <div className="app-muted max-w-[14rem] truncate text-xs">{quote.customer_email || "No email"}</div>
                      </td>
                      <td className="!align-top">{quote.owner_name || quote.owner_user_id || "—"}</td>
                      <td className="!align-top">
                        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusClass(quote.status)}`}>
                          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                          {formatStatusLabel(quote.status)}
                        </span>
                      </td>
                      <td className="!align-top">
                        {total > 0 ? (
                          <div className="w-28">
                            <div className="app-muted mb-1 flex justify-between text-xs tabular-nums">
                              <span>{done}/{total}</span>
                              <span>{pct}%</span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--app-pressed)]">
                              <div
                                className={`h-full rounded-full ${pct === 100 ? "bg-emerald-500" : "bg-sky-500"}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="app-muted text-xs">No checklist</span>
                        )}
                      </td>
                      <td className="app-muted whitespace-nowrap tabular-nums !align-top">{formatUpdated(quote.updated_at || quote.created_at)}</td>
                      <td className="!align-top">
                        <div className="flex items-center justify-end gap-0.5">
                          <Link
                            className="app-icon-btn hover:!text-sky-600 dark:hover:!text-sky-300"
                            to={`/cabinet-quotes/${quote._id}`}
                            title="Open quote"
                            aria-label="Open quote"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </Link>
                          <button
                            className="app-icon-btn hover:!bg-rose-500/10 hover:!text-rose-600 dark:hover:!text-rose-300"
                            type="button"
                            title="Delete quote"
                            aria-label="Delete quote"
                            onClick={(event) => onDeleteQuote(event, quote._id)}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

              {!loading && quotes.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={hasActiveFilters ? SearchOffIcon : FolderOpenIcon}
                      title={hasActiveFilters ? "No quotes match your filters" : "No cabinet quotes yet"}
                      description={hasActiveFilters ? "Try adjusting your search or filters." : "Create your first quote to get started."}
                      action={!hasActiveFilters ? newQuoteButton : null}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </PageShell>
  );
}

export default CabinetQuoteList;
